<template>
  <v-dialog v-model="internalOpen" persistent max-width="700">
    <v-card>
      <v-card-title class="headline">
        {{ task.title }}
        <v-chip v-if="isAutomatedTask(task)" x-small label color="purple" dark class="ml-2">
          <v-icon x-small left>flash_on</v-icon>{{ isAutomatedDocumentTask(task) ? 'Automated - Document' : 'Automated - Email' }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-select
              v-model="task.statusId"
              :items="availableStatusOptions"
              item-text="name"
              item-value="id"
              label="Status"
              outlined
              dense
              :readonly="isAutomatedTask(task)"
              :hint="isAutomatedTask(task) ? 'System-managed for automated steps' : ''"
              :persistent-hint="isAutomatedTask(task)"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="task.dueDate"
              label="Due Date"
              type="date"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" sm="6">
            <UsersDropDownList
              v-if="isAdmin"
              :userId="task.assignedToUserId"
              label="Assigned To"
              :clearable="false"
              :onlyActiveUsers="true"
              :hideDetails="true"
              @userIdChange="task.assignedToUserId = $event"
              @userNameChange="onAssignedUserNameChange"
            />
            <v-text-field
              v-else-if="task.assignedToUserName"
              :value="task.assignedToUserName"
              label="Assigned To"
              outlined
              dense
              readonly
            />
          </v-col>
          <v-col cols="12" v-if="task.description">
            <v-textarea
              :value="task.description"
              label="Description"
              outlined
              dense
              readonly
              rows="3"
              auto-grow
            />
          </v-col>
          <v-col cols="12" v-if="task.blockerTaskTitle">
            <v-text-field
              :value="task.blockerTaskTitle"
              label="Blocked By"
              outlined
              dense
              readonly
            />
          </v-col>
          <template v-if="isAutomatedTask(task)">
            <v-col cols="12" sm="6" v-if="task.executedAt">
              <v-text-field
                :value="task.executedAt | formatDate"
                label="Executed At"
                outlined
                dense
                readonly
              />
            </v-col>
            <v-col cols="12" sm="6" v-if="isAutomatedEmailTask(task) && task.reviewStatusId">
              <div class="caption grey--text">Review Status</div>
              <v-chip
                small
                label
                :color="reviewStatusAttrs(task.reviewStatusId).color"
                :dark="reviewStatusAttrs(task.reviewStatusId).dark"
              >{{ task.reviewStatusName }}</v-chip>
            </v-col>
            <v-col cols="12" sm="6" v-if="isAutomatedDocumentTask(task) && task.outputFileName">
              <div class="caption grey--text">Generated File</div>
              <div class="body-2"><v-icon small color="green" left>description</v-icon>{{ task.outputFileName }}</div>
            </v-col>
            <v-col cols="12" v-if="task.executionResult">
              <v-textarea
                :value="task.executionResult"
                label="Execution Result"
                outlined
                dense
                readonly
                rows="2"
              />
            </v-col>
          </template>
        </v-row>

        <v-divider class="my-3"></v-divider>
        <div class="subtitle-2 mb-2">Notes</div>

        <div v-if="notes && notes.length > 0" class="mb-3">
          <v-card
            v-for="note in notes"
            :key="note.id"
            outlined
            class="mb-2 pa-3"
          >
            <div class="body-2">{{ note.note }}</div>
            <div class="caption grey--text mt-1">{{ note.createdByUserName }} &mdash; {{ note.createdAt | formatDate }}</div>
          </v-card>
        </div>
        <div v-else class="grey--text caption mb-3">No notes yet.</div>

        <v-textarea
          v-model="newNote"
          label="Add a note"
          outlined
          dense
          rows="2"
          auto-grow
          hide-details
        />
        <v-btn
          class="mt-2"
          color="secondary"
          outlined
          small
          :disabled="!newNote || !newNote.trim()"
          @click="handleAddNote"
        >
          Add Note
        </v-btn>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="canReExecute"
          color="orange darken-1"
          outlined
          :loading="reExecuting"
          @click="handleReExecute"
        >
          Re-execute
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn outlined @click="handleClose">Cancel</v-btn>
        <v-btn color="primary" @click="handleSave">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { UsersDropDownList } from '@/components/users'
import { userHasRoles, getCurrentUser } from '@/lib/authentication'
import { ADMIN, SUPER_ADMIN, MANAGER } from '@/lib/constants/securityRoles'
import { WORKFLOW_TASK_STATUS_OPTIONS, workflowTaskStatus } from '@/lib/constants/workflowStatuses'
import workflowEmailReviewStatusMixin from '@/components/workflowEmailSends/workflowEmailReviewStatusMixin'
import workflowTaskAutomationMixin from './workflowTaskAutomationMixin'

export default {
  mixins: [workflowEmailReviewStatusMixin, workflowTaskAutomationMixin],
  components: {
    UsersDropDownList
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    taskId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      taskStatusOptions: WORKFLOW_TASK_STATUS_OPTIONS,
      newNote: '',
      task: {},
      reExecuting: false
    }
  },
  computed: {
    internalOpen: {
      get () { return this.value },
      set (val) { this.$emit('input', val) }
    },
    isAdmin () {
      return userHasRoles({ user: getCurrentUser(), allowedRoles: [ADMIN, SUPER_ADMIN] })
    },
    canCancelTask () {
      return userHasRoles({ user: getCurrentUser(), allowedRoles: [ADMIN, SUPER_ADMIN, MANAGER] })
    },
    availableStatusOptions () {
      if (this.canCancelTask) return this.taskStatusOptions
      return this.taskStatusOptions.filter(s => s.id !== 6)
    },
    canReExecute () {
      return this.isAdmin && this.isAutomatedTask(this.task) && this.task.statusId === workflowTaskStatus.OffTrack
    },
    ...mapFields('workflowTask', ['notes'])
  },
  watch: {
    value (opened) {
      if (opened && this.taskId) {
        this.loadTask()
      }
    }
  },
  methods: {
    ...mapActions('workflowTask', ['getWorkflowTask', 'getNotes', 'saveWorkflowTask', 'addTaskNote', 'clearWorkflowTask']),
    async loadTask () {
      const taskData = await this.getWorkflowTask({ id: this.taskId })
      if (taskData) {
        this.task = Object.assign({}, taskData)
        if (this.task.dueDate) {
          this.task.dueDate = this.task.dueDate.split('T')[0]
        }
      }
      await this.getNotes(this.taskId)
    },
    onAssignedUserNameChange (firstName, lastName) {
      this.task.assignedToUserName = `${firstName} ${lastName}`
    },
    async handleSave () {
      this.$store.commit('workflowTask/setWorkflowTask', this.task)
      try {
        await this.saveWorkflowTask()
        this.$emit('saved')
        this.handleClose()
      } catch (e) {
        // error already notified by store
      }
    },
    async handleReExecute () {
      this.reExecuting = true
      try {
        await this.$store.dispatch('workflowTask/reExecuteTask', this.taskId)
        await this.loadTask()
        this.$emit('saved')
      } catch (e) {
        // error already notified by store
      } finally {
        this.reExecuting = false
      }
    },
    async handleAddNote () {
      if (!this.newNote || !this.newNote.trim()) return
      try {
        await this.addTaskNote({ taskId: this.taskId, note: this.newNote.trim() })
        this.newNote = ''
      } catch (e) {
        // error already notified by store
      }
    },
    handleClose () {
      this.task = {}
      this.newNote = ''
      this.clearWorkflowTask()
      this.internalOpen = false
    }
  }
}
</script>
